import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import type { components } from "../../apis/gen";
import {
	ApiError,
	append_sessions,
	join_event,
	whoami,
} from "../../apis/helper";
import { BlockButton } from "../../inJoy-components/Button/BlockButton";
import { IconButton } from "../../inJoy-components/Button/IconButton";
import { Icon } from "../../inJoy-components/Icon/Icon";
import { ResponsiveModal } from "../../inJoy-components/Modal/ResponsiveModal";
import { Tab } from "../../inJoy-components/Tab/Tab";
import {
	connectSseThunk,
	fetchEventThunk,
} from "../../redux/events/events-thunks";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import type { RootState } from "../../redux/store";
import { saveReturnUrl } from "../../utils/auth";
import {
	calculateSessionScores,
	type SessionScore,
} from "../../utils/score-utils";
import {
	ControlDockTabContent,
	HeroActive,
	HeroCheckin,
	HeroEndgame,
	SessionSummaryTabContent,
	SummaryTabContent,
	VideoTabContent,
} from "./components";
import { EVENT_STATES, useEventState } from "./useEventState";

type Session = components["schemas"]["Session"];
type UserProfile = components["schemas"]["WhoamiPayload"];

/**
 * Event detail page component - displays event information, live updates, and controls
 */
const EventDetailPage = () => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useAppDispatch();

	const event = useAppSelector(
		(state: RootState) => state.events.events[id ?? ""],
	);

	const [loading, setLoading] = useState(true);
	const [currentTime, setCurrentTime] = useState(new Date());
	const [activeTab, setActiveTab] = useState(0);
	const [selectedSessionIdx, setSelectedSessionIdx] = useState<number | null>(
		null,
	);
	const [isShareModalOpen, setIsShareModalOpen] = useState(false);
	const [shareCopied, setShareCopied] = useState(false);
	const [resultModalType, setResultModalType] = useState<
		"success" | "login" | null
	>(null);

	const [me, setMe] = useState<UserProfile | null>(null);

	const showControl = me?.full_access ?? false;

	// fetch whoami
	useEffect(() => {
		if (!me) {
			const f = async () => {
				const me = await whoami();
				setMe(me);
			};
			f();
		}
	}, [me]);

	const hasJoined =
		me != null && event && event.participants.includes(me.user_id);

	// Use the custom hook for event state management
	const {
		eventState,
		countdown,
		isActiveState,
		isEndgameState,
		isEventEnded,
		canAddSession,
	} = useEventState(event, currentTime);

	// Handle hash-based session selection
	// Uses location.hash from useLocation to react to React Router navigation
	// Only depends on hash and session count to avoid resetting tabs on every SSE update
	const sessionCount = event?.sessions?.length ?? 0;
	useEffect(() => {
		const hash = location.hash;
		if (hash?.startsWith("#")) {
			const sessionNum = parseInt(hash.substring(1), 10);
			if (!Number.isNaN(sessionNum) && sessionNum > 0 && sessionCount > 0) {
				const idx = sessionNum - 1;
				if (idx >= 0 && idx < sessionCount) {
					setSelectedSessionIdx(idx);
					setActiveTab(0);
					return;
				}
			}
		}
		// No valid hash → event mode
		setSelectedSessionIdx(null);
		setActiveTab(0);
	}, [location.hash, sessionCount]);

	// Update current time every second for real-time state updates and countdowns
	useEffect(() => {
		if (event) {
			const now = Date.now();
			const endTime = event.ended_at
				? new Date(event.ended_at).getTime()
				: Infinity;

			if (now >= endTime) {
				// eslint-disable-next-line no-console
				console.log("Event has ended, not starting timer interval");
				setCurrentTime(new Date());
				return;
			}
		}

		const timer = setInterval(() => {
			if (event) {
				const now = Date.now();
				const endTime = event.ended_at
					? new Date(event.ended_at).getTime()
					: Infinity;

				if (now >= endTime) {
					// eslint-disable-next-line no-console
					console.log("Event ended, clearing timer interval");
					clearInterval(timer);
					setCurrentTime(new Date());
					return;
				}
			}
			setCurrentTime(new Date());
		}, 1000);

		return () => clearInterval(timer);
	}, [event]);

	// Fetch event data and connect to SSE for real-time updates
	useEffect(() => {
		if (!id) return;

		const fetchPromise = dispatch(fetchEventThunk(id));
		const ssePromise = dispatch(connectSseThunk(id));

		fetchPromise
			.unwrap()
			.catch((error) => {
				console.error("Error fetching event details:", error);
				if (error?.status === 401 || error?.status === 403) {
					saveReturnUrl();
					navigate("/login");
				}
			})
			.finally(() => {
				setLoading(false);
			});

		return () => {
			fetchPromise.abort();
			ssePromise.abort();
		};
	}, [id, dispatch, navigate]);

	/**
	 * Index of the currently active session based on current time.
	 * Memoised on a stable primitive so downstream consumers only
	 * re-render when the *active session actually changes*, not on
	 * every 1-second tick or SSE event update.
	 */
	const currentSessionIdx = useMemo((): number => {
		if (!event || !event.sessions || event.sessions.length === 0) return -1;

		const now = currentTime.getTime();

		for (let i = 0; i < event.sessions.length; i++) {
			const session = event.sessions[i];
			const sessionStart = new Date(session.started_at).getTime();
			const sessionEnd = new Date(session.ended_at).getTime();

			if (now >= sessionStart && now <= sessionEnd) {
				return i;
			}
		}

		return -1;
	}, [event, currentTime]);

	const currentSession: Session | null =
		currentSessionIdx >= 0 && event?.sessions
			? event.sessions[currentSessionIdx]
			: null;

	const calculateTotalScores = useCallback((): SessionScore[] => {
		if (!event?.sessions?.length) return [];
		return event.sessions
			.filter((s) => s.result_details)
			.map((s) => calculateSessionScores(s.result_details));
	}, [event]);

	/**
	 * Create a new game (session) for the event
	 */
	const handleCreateGame = async (): Promise<void> => {
		if (!id) return;

		try {
			const gameIndex = (event?.sessions?.length || 0) + 1;

			let startedAfterSeconds = 600;
			if (event?.sessions?.length && event.sessions.length > 0) {
				const latestSession = event.sessions.reduce((latest, session) => {
					const sessionEnd = new Date(session.ended_at).getTime();
					const latestEnd = new Date(latest.ended_at).getTime();
					return sessionEnd > latestEnd ? session : latest;
				});
				const latestEndTime = new Date(latestSession.ended_at).getTime();
				const now = Date.now();
				startedAfterSeconds = Math.max(
					0,
					Math.floor((latestEndTime - now) / 1000) + 600,
				);
			}

			const sessionData = {
				name: `test game ${gameIndex}`,
				desc: `Random test description ${Math.random().toString(36).substring(7)}`,
				duration_seconds: 600,
				started_after_seconds: startedAfterSeconds,
			};

			await append_sessions(id, sessionData);
			console.log("Game created successfully");

			// SSE will automatically receive session_appended and re-fetch,
			// but we can also trigger a manual refresh
			dispatch(fetchEventThunk(id));
		} catch (error) {
			console.error("Error creating game:", error);
			if (
				error instanceof ApiError &&
				(error.status === 401 || error.status === 403)
			) {
				saveReturnUrl();
				navigate("/login");
			}
		}
	};

	/**
	 * Open fullscreen kiosk mode and navigate to kiosk page
	 */
	const openFullscreenKiosk = useCallback(async (): Promise<void> => {
		try {
			const docEl = document.documentElement as HTMLElement & {
				webkitRequestFullscreen?: () => Promise<void>;
				mozRequestFullScreen?: () => Promise<void>;
				msRequestFullscreen?: () => Promise<void>;
			};

			if (docEl.requestFullscreen) {
				await docEl.requestFullscreen();
			} else if (docEl.webkitRequestFullscreen) {
				await docEl.webkitRequestFullscreen();
			} else if (docEl.mozRequestFullScreen) {
				await docEl.mozRequestFullScreen();
			} else if (docEl.msRequestFullscreen) {
				await docEl.msRequestFullscreen();
			}

			const kioskUrl = `/events/${id}/kiosk`;
			window.location.href = kioskUrl;
		} catch (err) {
			console.error("Failed to enter fullscreen:", err);
			const kioskUrl = `/events/${id}/kiosk`;
			window.location.href = kioskUrl;
		}
	}, [id]);

	/**
	 * Handle "Receive Game Results" button click
	 * Checks auth status and either joins event or prompts login
	 */
	const handleReceiveResults = useCallback(async (): Promise<void> => {
		if (!id) return;

		try {
			await join_event(id);

			setResultModalType("success");
		} catch (error) {
			if (
				error instanceof ApiError &&
				(error.status === 401 || error.status === 403)
			) {
				setResultModalType("login");
			} else {
				// eslint-disable-next-line no-console
				console.error("Error joining event:", error);
			}
		}
	}, [id]);

	const tabEntries =
		selectedSessionIdx === null || !showControl
			? ["Summary", "Videos"]
			: ["Summary", "Videos", "Control Dock"];

	/**
	 * Handle opening the share modal
	 */
	const handleOpenShareModal = useCallback((): void => {
		setIsShareModalOpen(true);
	}, []);

	if (loading || eventState === EVENT_STATES.LOADING) {
		return (
			<div className="p-4">
				<p>Loading event details...</p>
			</div>
		);
	}

	if (!event || eventState === EVENT_STATES.NOT_FOUND) {
		return (
			<div className="p-4">
				<Link
					to="/events"
					className="text-blue-600 hover:text-blue-800 mb-4 inline-block"
				>
					&larr; Back to Events
				</Link>
				<p>Event not found.</p>
			</div>
		);
	}

	return (
		<div className={`${hasJoined ? "pb-4" : "pb-[154px]"} lg:pb-0`}>
			{/* ==== HERO SECTION ==== */}
			{(eventState === EVENT_STATES.WAITING_UNCHECKED_IN ||
				eventState === EVENT_STATES.IDLING) && (
				<HeroCheckin
					event={event}
					selectedSessionIdx={selectedSessionIdx}
					onShare={handleOpenShareModal}
					onReceiveResults={handleReceiveResults}
					hasJoined={hasJoined}
				/>
			)}

			{isActiveState && (
				<HeroActive
					event={event}
					selectedSessionIdx={selectedSessionIdx}
					onAddSession={handleCreateGame}
					canAddSession={canAddSession}
					eventState={eventState}
					countdown={countdown}
					onShare={handleOpenShareModal}
					onReceiveResults={handleReceiveResults}
					hasJoined={hasJoined}
				/>
			)}

			{(isEndgameState || isEventEnded) && (
				<HeroEndgame
					event={event}
					eventState={eventState}
					selectedSessionIdx={selectedSessionIdx}
					calculateTotalScores={calculateTotalScores}
					onAddSession={handleCreateGame}
					canAddSession={canAddSession}
					onShare={handleOpenShareModal}
					onReceiveResults={handleReceiveResults}
					hasJoined={hasJoined}
				/>
			)}

			{/* ==== TAB ==== */}
			<div className="sticky top-[72px] left-[0] z-30 w-full bg-white">
				<Tab
					className="w-fit px-5 lg:px-[calc(50vw-600px)] py-[20px] lg:py-[12px] shadow-transparent"
					tabs={tabEntries}
					activeTab={activeTab}
					setActiveTab={setActiveTab}
				/>
			</div>

			{/* ==== SUMMARY ==== */}
			{activeTab === 0 && selectedSessionIdx === null && (
				<SummaryTabContent event={event} />
			)}

			{activeTab === 0 && selectedSessionIdx !== null && (
				<SessionSummaryTabContent
					event={event}
					selectedSessionIdx={selectedSessionIdx}
				/>
			)}

			{/* ==== VIDEO ==== */}
			{activeTab === 1 && (
				<VideoTabContent
					event={event}
					selectedSessionIdx={selectedSessionIdx}
				/>
			)}

			{/* ==== SESSION CONTROL DOCK ==== */}
			{showControl && activeTab === 2 && (
				<ControlDockTabContent
					eventState={eventState}
					currentSession={currentSession}
					activeTab={activeTab}
					openFullscreenKiosk={openFullscreenKiosk}
				/>
			)}

			{/* ==== Mobile Fixed Button ==== */}
			{!hasJoined && (
				<div className="lg:hidden fixed z-10 left-[0] right-[0] bottom-[0] px-5 py-3 border-t border-color-Grey100 bg-white">
					<BlockButton
						type="Primary"
						width="100%"
						icon=""
						size="Large"
						onClick={handleReceiveResults}
						disabled={false}
						loading={false}
						loadingIcon=""
					>
						Receive Game Results
					</BlockButton>
				</div>
			)}

			{/* ==== SHARE MODAL ==== */}
			<ResponsiveModal
				isOpen={isShareModalOpen}
				onClose={() => setIsShareModalOpen(false)}
			>
				{/* Modal Header */}
				<div className="flex w-full flex-col gap-[8px] items-start">
					<p className="w-full text-[20px] font-semibold leading-[1.2] tracking-[-0.5px] text-[#47515f]">
						Share this game with your friends
					</p>
					<p className="w-full text-[16px] font-medium leading-[1.25] text-[#b1b1b1]">
						Share the game results and highlight videos with them.
					</p>
				</div>

				{/* Modal Footer */}
				<div className="flex w-full flex-col gap-[20px] items-start pt-[16px]">
					{/* Share Buttons */}
					<div className="flex w-full flex-col gap-[8px] items-start">
						<BlockButton
							type="Primary"
							size="Large"
							icon="threads_icon"
							width="100%"
							fillLabel={true}
							disabled={false}
							loading={false}
							loadingIcon=""
							onClick={() => {
								const url = encodeURIComponent(window.location.href);
								window.open(
									`https://www.threads.net/intent/post?text=${url}`,
									"_blank",
								);
							}}
						>
							Share on Threads
						</BlockButton>
						<BlockButton
							type="Primary"
							size="Large"
							icon="line_icon"
							width="100%"
							fillLabel={true}
							disabled={false}
							loading={false}
							loadingIcon=""
							onClick={() => {
								const url = encodeURIComponent(window.location.href);
								window.open(
									`https://social-plugins.line.me/lineit/share?url=${url}`,
									"_blank",
								);
							}}
						>
							Share on LINE
						</BlockButton>
						<BlockButton
							type="Primary"
							size="Large"
							icon="facebook_icon"
							width="100%"
							fillLabel={true}
							disabled={false}
							loading={false}
							loadingIcon=""
							onClick={() => {
								const url = encodeURIComponent(window.location.href);
								window.open(
									`https://www.facebook.com/sharer/sharer.php?u=${url}`,
									"_blank",
								);
							}}
						>
							Share on Facebook
						</BlockButton>
					</div>

					{/* Copy URL */}
					<button
						onClick={async () => {
							try {
								await navigator.clipboard.writeText(window.location.href);
								setShareCopied(true);
								setTimeout(() => setShareCopied(false), 2000);
							} catch (err) {
								// eslint-disable-next-line no-console
								console.error("Failed to copy URL:", err);
							}
						}}
						className="relative h-[48px] w-full rounded-[8px] transition-colors hover:bg-gray-50"
					>
						<div className="flex h-full w-full items-center justify-center gap-[16px] overflow-clip rounded-[inherit] px-[12px] py-[16px]">
							<p className="flex-1 truncate text-center text-[16px] font-normal tracking-[0.25px] text-[#404040]">
								{window.location.href}
							</p>
							<IconButton
								type="Tertiary"
								icon={shareCopied ? "check_outline" : "copy_outline"}
								iconSize="Large"
							/>
						</div>
						<div
							aria-hidden="true"
							className="pointer-events-none absolute inset-0 rounded-[8px] border border-solid border-[#dfdfdf]"
						/>
					</button>
				</div>
			</ResponsiveModal>

			{/* ==== RECEIVE RESULTS SUCCESS MODAL ==== */}
			<ResponsiveModal
				isOpen={resultModalType === "success"}
				onClose={() => setResultModalType(null)}
			>
				<div className="flex w-full flex-col gap-2 items-center text-center">
					<Icon iconName="check_outline" size="XXXLarge" color="#1EB44A" />
					<p className="mt-4 w-full text-[20px] font-semibold leading-[1.2] tracking-[-0.5px] text-[#47515f]">
						You're all set!
					</p>
					<p className="w-full text-[16px] font-medium leading-[1.25] text-[#b1b1b1]">
						We'll send you the highlight and full game video after the game
						ends.
					</p>
				</div>
				<div className="flex w-full flex-col gap-[8px] items-start pt-[16px]">
					<BlockButton
						type="Secondary"
						size="Large"
						icon=""
						width="100%"
						disabled={false}
						loading={false}
						loadingIcon=""
						onClick={() => setResultModalType(null)}
					>
						Close
					</BlockButton>
				</div>
			</ResponsiveModal>

			{/* ==== LOGIN PROMPT MODAL ==== */}
			<ResponsiveModal
				isOpen={resultModalType === "login"}
				onClose={() => setResultModalType(null)}
			>
				<div className="flex w-full flex-col gap-2 items-center text-center">
					<img
						className="w-[240px]"
						src={`${import.meta.env.VITE_APP_WEBSITE_URL}/img/event-page/game_info_thumbnail.png`}
						alt="promo thumbnail"
					/>
					<p className="w-full text-[20px] font-semibold leading-[1.2] tracking-[-0.5px] text-[#47515f]">
						Log in to receive game result
					</p>
					<p className="w-full text-[16px] font-medium leading-[1.25] text-[#b1b1b1]">
						The game videos and highlight clips will be shared to your inJoy
						library after game ends
					</p>
				</div>
				<div className="flex w-full flex-col gap-[8px] items-start pt-[16px]">
					<BlockButton
						type="Primary"
						size="Large"
						icon=""
						width="100%"
						disabled={false}
						loading={false}
						loadingIcon=""
						onClick={() => {
							setResultModalType(null);
							saveReturnUrl();
							navigate("/login");
						}}
					>
						Log In / Sign Up
					</BlockButton>
					<BlockButton
						type="Secondary"
						size="Large"
						icon=""
						width="100%"
						disabled={false}
						loading={false}
						loadingIcon=""
						onClick={() => setResultModalType(null)}
					>
						Maybe Later
					</BlockButton>
				</div>
			</ResponsiveModal>
		</div>
	);
};

export { EventDetailPage };
